<template>
  <li
    class="nr"
    :class="[toneClass, { 'is-unread': unread, 'is-clickable': isClickable }]"
    @click="$emit('open', item)"
  >
    <v-checkbox
      v-if="selectable"
      :input-value="selected"
      dense
      hide-details
      class="nr__check"
      @click.stop
      @change="$emit('select', item.id)"
    />

    <span class="nr__icon">
      <component
        :is="getNotificationIcon(item.eventType)"
        :size="16"
      />
    </span>

    <div class="nr__body">
      <div class="nr__top">
        <span class="nr__label">{{ getNotificationLabel(item.eventType) }}</span>
        <span class="nr__time">{{ relativeTime }}</span>
      </div>

      <p class="nr__msg">{{ item.message }}</p>

      <p
        v-if="detail"
        class="nr__detail"
      >
        {{ detail }}
      </p>
    </div>

    <span
      v-if="unread"
      class="nr__dot"
      aria-hidden="true"
    ></span>
  </li>
</template>

<script>
import notificationPresentation from '@/mixin/mixin-notification-presentation';
import notificationNavigation from '@/mixin/mixin-notification-navigation';

export default {
  name: 'NotificationRow',
  mixins: [notificationPresentation, notificationNavigation],
  props: {
    item: {
      type: Object,
      required: true
    },
    unread: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    toneClass() {
      return this.getNotificationToneClass(this.item.eventType);
    },

    /* Só finge ser clicável o que leva a algum lugar. Receiver e ficha
       assinada apenas informam — cursor de mão neles promete uma navegação
       que não existe. */
    isClickable() {
      return this.selectable || this.hasNotificationTarget(this.item.eventType);
    },

    detail() {
      return this.item.customerMessage || this.item.userMessage || null;
    },

    relativeTime() {
      const value = this.item.createdAt || this.item.date;

      if (!value) return '';

      const diffMs = Date.now() - new Date(value).getTime();
      const diffMin = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMin < 1) return this.$t('common.time.now');

      if (diffMin < 60) return `${diffMin} ${this.$t('common.time.minuteAbbr')}`;

      if (diffHours < 24) return `${diffHours} ${this.$t('common.time.hourAbbr')}`;

      if (diffDays < 7) return `${diffDays} ${this.$t('common.time.dayAbbr')}`;

      return new Date(value).toLocaleDateString(this.$i18n.locale, { day: '2-digit', month: 'short' });
    }
  }
};
</script>

<style scoped>
/* Tom semântico do evento. Cada variante só troca as duas variáveis abaixo —
   o resto do layout não sabe de cor. */
.nr.is-action {
  --n-tone: var(--tc-action);
  --n-tone-soft: var(--tc-action-tint-strong);
}

.nr.is-success {
  --n-tone: var(--tc-success);
  --n-tone-soft: var(--tc-success-soft);
}

.nr.is-warning {
  --n-tone: var(--tc-warning);
  --n-tone-soft: var(--tc-warning-soft);
}

.nr.is-danger {
  --n-tone: var(--tc-danger);
  --n-tone-soft: var(--tc-danger-soft);
}

.nr.is-info {
  --n-tone: var(--tc-info);
  --n-tone-soft: var(--tc-info-soft);
}

.nr.is-neutral {
  --n-tone: var(--tc-text-2);
  --n-tone-soft: var(--tc-neutral-soft);
}

.nr {
  display: flex;
  align-items: flex-start;
  gap: var(--tc-space-3);
  padding: var(--tc-space-3) var(--tc-space-2);
  border-radius: var(--tc-radius-sm);
  transition: background-color var(--tc-transition);
}

.nr.is-clickable {
  cursor: pointer;
}

.nr.is-clickable:hover {
  background: var(--tc-row-hover);
}

.nr__check {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}

/* Selo quadrado arredondado, plano. O gradiente que existia aqui é exceção
   reservada ao painel da Aura — fora dele a marca não usa gradiente. */
.nr__icon {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--tc-radius-sm);
  background: var(--n-tone-soft);
  color: var(--n-tone);
}

.nr__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nr__top {
  display: flex;
  align-items: baseline;
  gap: var(--tc-space-2);
}

/* Rótulo mono MAIÚSCULO no lugar do chip colorido: chip-pílula decorativo é
   anti-padrão do DS, e eram 12 cores diferentes competindo numa lista. */
.nr__label {
  flex: 1 1 auto;
  min-width: 0;
  font-family: var(--tc-font-mono);
  font-size: var(--tc-fs-label);
  font-weight: var(--tc-fw-medium);
  letter-spacing: var(--tc-tracking-label);
  text-transform: uppercase;
  color: var(--n-tone);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nr__time {
  flex: 0 0 auto;
  font-family: var(--tc-font-mono);
  font-size: var(--tc-fs-label);
  font-variant-numeric: tabular-nums;
  color: var(--tc-text-3);
  white-space: nowrap;
}

.nr__msg {
  margin: 0;
  font-size: var(--tc-fs-sm);
  font-weight: var(--tc-fw-regular);
  line-height: var(--tc-lh-snug);
  color: var(--tc-text-2);
}

/* Não lida pesa 500 e usa o texto forte. O tratamento antigo era o inverso —
   opacidade 0.55 na lida inteira, que apagava também o que ela diz. */
.nr.is-unread .nr__msg {
  font-weight: var(--tc-fw-medium);
  color: var(--tc-text);
}

.nr__detail {
  margin: 0;
  font-size: var(--tc-fs-sm);
  line-height: var(--tc-lh-snug);
  color: var(--tc-text-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nr__dot {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  margin-top: 13px;
  border-radius: var(--tc-radius-pill);
  background: var(--n-tone);
}
</style>
